import { TrialRelanceMoment, TrialVoie } from '../translation_namespace/trial_relance_namespace';

/**
 * Registre de la relance des essais — docs/to_implement/trial-relance-plan.md §1 et §2.
 *
 * Deux voies (déclarées par l'intention du wizard, `LES_DEUX` suit la voie Site), quatre
 * étages, un score de propension 0-100 par voie. Chaque tâche d'essai est une SOUS-ACTION
 * d'un étage avec son poids en points ; c'est la sous-action non faite au plus fort poids
 * restant que visent le bandeau in-app et les relances (tranché le 08/09 : même règle que
 * `pickNudge` sur le tableau de bord).
 *
 * Volontairement distinct de `ONBOARDING_REGISTRY` : le score d'onboarding mesure la
 * complétion (import compris), le score d'essai mesure la PERSONNALISATION après la borne
 * `wizard_left_at`. Les évaluateurs vivent côté backend (`trial-progress.engine.ts`) ; ce
 * fichier ne porte que la structure, les poids et la navigation.
 */

export type TrialStage = 0 | 1 | 2 | 3 | 4;

export interface TrialTask {
  /** Identifiant stable de la sous-action — clé de la bibliothèque de contenus et des snapshots. */
  id: string;
  stage: 1 | 2 | 3 | 4;
  /** Points au score de propension (0 = jalon ou action sans poids, ordonnée par `order`). */
  points: number;
  /** Ordre de priorité à points égaux (1 = d'abord). */
  order: number;
  /** Libellé du bandeau (namespace `TRIAL_RELANCE`, cf. messages A.5). */
  label_key: string;
  /** Libellé du bouton du bandeau / CTA email. */
  cta_key: string;
  /** Deep link dans l'app pro. */
  href: string;
  /** Tâche d'onboarding correspondante quand elle existe (pour la cohérence avec le score). */
  registry_task_id?: string;
  /** Jalon : franchit l'étage à lui seul (étage 3) — cf. `isStageReached`. */
  milestone?: boolean;
}

export interface TrialStageDef {
  stage: 1 | 2 | 3 | 4;
  /** Clé de libellé de l'étage (« Catalogue », « Design »…). */
  label_key: string;
  /**
   * Mode de franchissement : `block` = bloc de points ≥ 50 % ET aucune action restante plus
   * rentable que la meilleure de l'étage suivant ; `milestone` = un jalon posé ; `first_action`
   * = dès la première action.
   */
  reached_by: 'block' | 'milestone' | 'first_action';
}

/** Socle : points acquis quand le wizard est terminé. */
export const TRIAL_WIZARD_DONE_POINTS = 5;
/** Plafond du score de propension. */
export const TRIAL_SCORE_CAP = 100;
/** Seuil de franchissement d'un étage à bloc de points. */
export const TRIAL_STAGE_BLOCK_THRESHOLD = 0.5;
/** Au-dessus de ce score, le bandeau in-app se tait (plan §6). */
export const TRIAL_BANNER_HIDE_ABOVE_SCORE = 85;
/** Seuils de segment (plan §4, à recalibrer après 30 inscrits). */
export const TRIAL_SEGMENT_READY_SCORE = 60;
export const TRIAL_SEGMENT_BUILDING_SCORE = 25;

/**
 * Ce sur quoi porte VRAIMENT le message d'un moment.
 *
 * L'écran admin « À venir » affichait la sous-action courante du pro sur TOUTES les lignes (elle
 * vient de l'état, pas du message) : un pro bloqué à l'étage Catalogue semblait recevoir « ajoutez
 * vos prix » six jours de suite, alors que J6/J6-SMS/J7 ne parlent que de la fin d'essai et que
 * l'angle tourne (1 → 2 → 3) d'un email à l'autre sur un même étage.
 */
export enum TrialMomentFocus {
  /** Le message pousse la sous-action au plus fort poids restant (angle 1 → 2 → 3). */
  ACTION = 'ACTION',
  /** J5 : cadre de fin d'essai (CTA « Choisir ma formule ») + bloc de l'étage en second. */
  ACTION_AND_CLOSING = 'ACTION_AND_CLOSING',
  /** J6 / J6-SMS / J7 : fin d'essai et formule uniquement, jamais la sous-action. */
  CLOSING = 'CLOSING',
  /** SMS J0+4h et SMS J2 (Fantôme) : reprise de la configuration / démo. */
  RESUME = 'RESUME',
}

export const TRIAL_MOMENT_FOCUS: Record<TrialRelanceMoment, TrialMomentFocus> = {
  [TrialRelanceMoment.J0_SMS]: TrialMomentFocus.RESUME,
  [TrialRelanceMoment.J1]: TrialMomentFocus.ACTION,
  [TrialRelanceMoment.J2]: TrialMomentFocus.ACTION,
  [TrialRelanceMoment.J2_SMS]: TrialMomentFocus.RESUME,
  [TrialRelanceMoment.J3]: TrialMomentFocus.ACTION,
  [TrialRelanceMoment.J5]: TrialMomentFocus.ACTION_AND_CLOSING,
  [TrialRelanceMoment.J6]: TrialMomentFocus.CLOSING,
  [TrialRelanceMoment.J6_SMS]: TrialMomentFocus.CLOSING,
  [TrialRelanceMoment.J7]: TrialMomentFocus.CLOSING,
};

// ─── Sous-actions communes ──────────────────────────────────────────────────────

const CATALOG_HREF = '/catalog/prestations';
const AVAILABILITIES_HREF = '/agenda/availabilities';
const BUILDER_HREF = '/website/builder';

/**
 * Étage 4 · Clients — identique dans les deux voies (bonus plafonné).
 *
 * ⚠️ Pas de sous-action « première campagne » ici : l'envoi marketing est bloqué DUR pendant
 * l'essai (`isMarketingAllowed` renvoie false en TRIALING, `pro_campaign.service` throw
 * `SENDING_BLOCKED_DURING_TRIAL`, et une campagne programmée repasse en DRAFT au moment de
 * l'envoi). La proposer, c'était pousser le pro vers un mur ET rendre le score inatteignable :
 * la tâche ne pouvait jamais être franchie. À réintroduire le jour où l'essai autorise un
 * envoi de découverte.
 */
const CLIENTS_MARKETING_TASKS: TrialTask[] = [
  {
    id: 'customers.import',
    stage: 4,
    points: 5,
    order: 1,
    label_key: 'TASK_CUSTOMERS_IMPORT',
    cta_key: 'CTA_IMPORT',
    href: '/customers',
    registry_task_id: 'customers.base.import',
  },
  {
    id: 'customers.tags',
    stage: 4,
    points: 2,
    order: 3,
    label_key: 'TASK_CUSTOMERS_TAGS',
    cta_key: 'CTA_ORGANIZE',
    href: '/customers/tags',
    registry_task_id: 'customers.tags.organize',
  },
];

// ─── Voie Site ──────────────────────────────────────────────────────────────────

const SITE_TASKS: TrialTask[] = [
  // Étage 1 · Catalogue (70 pts) — le site vit des prestations.
  {
    id: 'catalog.prices',
    stage: 1,
    points: 25,
    order: 1,
    label_key: 'TASK_CATALOG_PRICES',
    cta_key: 'CTA_GO',
    href: CATALOG_HREF,
    registry_task_id: 'catalog.prestations.complete',
  },
  {
    id: 'catalog.descriptions',
    stage: 1,
    points: 15,
    order: 3,
    label_key: 'TASK_CATALOG_DESCRIPTIONS',
    cta_key: 'CTA_GO',
    href: CATALOG_HREF,
    registry_task_id: 'catalog.prestations.seo_descriptions',
  },
  {
    id: 'catalog.photos',
    stage: 1,
    points: 15,
    order: 4,
    label_key: 'TASK_CATALOG_PHOTOS',
    cta_key: 'CTA_GO',
    href: CATALOG_HREF,
    registry_task_id: 'catalog.prestations.real_photos',
  },
  {
    id: 'catalog.durations',
    stage: 1,
    points: 10,
    order: 2,
    label_key: 'TASK_CATALOG_DURATIONS',
    cta_key: 'CTA_GO',
    href: CATALOG_HREF,
  },
  {
    id: 'catalog.categories',
    stage: 1,
    points: 5,
    order: 5,
    label_key: 'TASK_CATALOG_CATEGORIES',
    cta_key: 'CTA_GO',
    href: CATALOG_HREF,
  },
  // Étage 2 · Design / appropriation (30 pts) — page > composant > couleur.
  {
    id: 'website.page_added',
    stage: 2,
    points: 9,
    order: 1,
    label_key: 'TASK_WEBSITE_PAGE_ADDED',
    cta_key: 'CTA_OPEN_SITE',
    href: BUILDER_HREF,
    registry_task_id: 'website.customize',
  },
  {
    id: 'website.component_customized',
    stage: 2,
    points: 8,
    order: 2,
    label_key: 'TASK_WEBSITE_COMPONENT',
    cta_key: 'CTA_OPEN_SITE',
    href: BUILDER_HREF,
    registry_task_id: 'website.customize',
  },
  {
    id: 'website.theme',
    stage: 2,
    points: 6,
    order: 3,
    label_key: 'TASK_WEBSITE_THEME',
    cta_key: 'CTA_OPEN_SITE',
    href: BUILDER_HREF,
  },
  {
    id: 'website.branding',
    stage: 2,
    points: 5,
    order: 4,
    label_key: 'TASK_WEBSITE_BRANDING',
    cta_key: 'CTA_GO',
    href: '/establishment/media/edit',
    registry_task_id: 'company.media.logo',
  },
  {
    id: 'website.builder_visited',
    stage: 2,
    points: 2,
    order: 5,
    label_key: 'TASK_WEBSITE_BUILDER_VISITED',
    cta_key: 'CTA_OPEN_SITE',
    href: BUILDER_HREF,
  },
  // Étage 3 · Agenda + publication — jalon : site publié (sous-domaine, possible en essai).
  {
    id: 'agenda.hours',
    stage: 3,
    points: 0,
    order: 1,
    label_key: 'TASK_AGENDA_HOURS_CHECK',
    cta_key: 'CTA_CHECK_HOURS',
    href: AVAILABILITIES_HREF,
    registry_task_id: 'agenda.hours.opening_hours',
  },
  {
    id: 'website.publish',
    stage: 3,
    points: 0,
    order: 2,
    label_key: 'TASK_WEBSITE_PUBLISH',
    cta_key: 'CTA_PUBLISH',
    href: '/',
    registry_task_id: 'website.publish',
    milestone: true,
  },
  ...CLIENTS_MARKETING_TASKS,
];

// ─── Voie Réservation ───────────────────────────────────────────────────────────

const RESERVATION_TASKS: TrialTask[] = [
  // Étage 1 · Agenda (40 pts) — horaires modifiés vs grille sectorielle, dispos, créneaux, sync.
  {
    id: 'agenda.hours',
    stage: 1,
    points: 15,
    order: 1,
    label_key: 'TASK_AGENDA_HOURS',
    cta_key: 'CTA_SET_HOURS',
    href: AVAILABILITIES_HREF,
    registry_task_id: 'agenda.hours.opening_hours',
  },
  {
    id: 'agenda.team_places',
    stage: 1,
    points: 10,
    order: 2,
    label_key: 'TASK_AGENDA_TEAM_PLACES',
    cta_key: 'CTA_SET_HOURS',
    href: AVAILABILITIES_HREF,
    registry_task_id: 'agenda.hours.team_availability',
  },
  {
    id: 'agenda.slot_params',
    stage: 1,
    points: 10,
    order: 3,
    label_key: 'TASK_AGENDA_SLOT_PARAMS',
    cta_key: 'CTA_GO',
    href: '/agenda/settings',
    registry_task_id: 'agenda.settings.review',
  },
  {
    id: 'agenda.google_sync',
    stage: 1,
    points: 5,
    order: 4,
    label_key: 'TASK_AGENDA_GOOGLE_SYNC',
    cta_key: 'CTA_GO',
    href: '/agenda/settings/sync',
    registry_task_id: 'agenda.google_sync.connect',
  },
  // Étage 2 · Catalogue (45 pts) + Paiement (15 pts).
  {
    id: 'catalog.prices',
    stage: 2,
    points: 25,
    order: 1,
    label_key: 'TASK_CATALOG_PRICES',
    cta_key: 'CTA_GO',
    href: CATALOG_HREF,
    registry_task_id: 'catalog.prestations.complete',
  },
  {
    id: 'catalog.durations',
    stage: 2,
    points: 10,
    order: 2,
    label_key: 'TASK_CATALOG_DURATIONS',
    cta_key: 'CTA_GO',
    href: CATALOG_HREF,
  },
  {
    id: 'catalog.descriptions_photos',
    stage: 2,
    points: 10,
    order: 3,
    label_key: 'TASK_CATALOG_DESCRIPTIONS_PHOTOS',
    cta_key: 'CTA_GO',
    href: CATALOG_HREF,
    registry_task_id: 'catalog.prestations.complete',
  },
  {
    id: 'payments.stripe',
    stage: 2,
    points: 10,
    order: 4,
    label_key: 'TASK_PAYMENTS_STRIPE',
    cta_key: 'CTA_ACTIVATE_PAYMENTS',
    href: '/establishment/stripe-connect',
    registry_task_id: 'payments.stripe.create_account',
  },
  {
    id: 'payments.deposit',
    stage: 2,
    points: 5,
    order: 5,
    label_key: 'TASK_PAYMENTS_DEPOSIT',
    cta_key: 'CTA_ACTIVATE_DEPOSIT',
    href: CATALOG_HREF,
    registry_task_id: 'payments.conditions.configure',
  },
  // Étage 3 · Traction — jalons : dispos personnalisées, premier RDV ajouté (saisi ou reçu).
  {
    id: 'agenda.first_appointment',
    stage: 3,
    points: 0,
    order: 1,
    label_key: 'TASK_AGENDA_FIRST_APPOINTMENT',
    cta_key: 'CTA_ADD_APPOINTMENT',
    href: '/agenda',
    milestone: true,
  },
  ...CLIENTS_MARKETING_TASKS,
];

export const TRIAL_STAGES: Record<TrialVoie, TrialStageDef[]> = {
  [TrialVoie.SITE]: [
    { stage: 1, label_key: 'STAGE_SITE_1', reached_by: 'block' },
    { stage: 2, label_key: 'STAGE_SITE_2', reached_by: 'block' },
    { stage: 3, label_key: 'STAGE_SITE_3', reached_by: 'milestone' },
    { stage: 4, label_key: 'STAGE_4', reached_by: 'first_action' },
  ],
  [TrialVoie.RESERVATION]: [
    { stage: 1, label_key: 'STAGE_RESA_1', reached_by: 'block' },
    { stage: 2, label_key: 'STAGE_RESA_2', reached_by: 'block' },
    { stage: 3, label_key: 'STAGE_RESA_3', reached_by: 'milestone' },
    { stage: 4, label_key: 'STAGE_4', reached_by: 'first_action' },
  ],
};

export const TRIAL_TASKS: Record<TrialVoie, TrialTask[]> = {
  [TrialVoie.SITE]: SITE_TASKS,
  [TrialVoie.RESERVATION]: RESERVATION_TASKS,
};

/** Tous les identifiants de sous-action, toutes voies confondues (pour les évaluateurs backend). */
export const TRIAL_TASK_IDS: ReadonlyArray<string> = Array.from(
  new Set([...SITE_TASKS, ...RESERVATION_TASKS].map((task): string => task.id)),
);

/** Total de points d'un étage à bloc (hors jalons). */
export function trialStagePoints(voie: TrialVoie, stage: TrialStage): number {
  return TRIAL_TASKS[voie]
    .filter((task): boolean => task.stage === stage)
    .reduce((sum, task): number => sum + task.points, 0);
}
